import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, GraduationCap, MapPin, Briefcase, Quote, Play } from 'lucide-react';
import { studentStories, getFeaturedStories, getStoryById } from '../data/studentStoriesData';
import { SEO, pageSEO } from '../components/SEO';

// Stories List View
function StoriesList() {
  const [selectedProgram, setSelectedProgram] = useState('All');
  const featuredStories = getFeaturedStories();

  const programs = ['All', ...new Set(studentStories.map(s => s.program))];

  const filteredStories = selectedProgram === 'All' 
    ? studentStories 
    : studentStories.filter(s => s.program === selectedProgram);

  return (
    <div data-testid="stories-page" className="min-h-screen pt-20">
      <SEO {...pageSEO.stories} url="/stories" />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#C5A028]">
        <div className="absolute inset-0 pattern-overlay opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <h1 className="text-5xl lg:text-6xl font-bold text-[#0D4A2E] mb-4">
            Student <span className="text-white">Stories</span>
          </h1>
          <p className="text-[#0D4A2E]/80 text-xl max-w-2xl">
            Discover how CEPRES graduates are transforming their communities and careers
          </p>
        </div>
      </section>

      {/* Featured Stories */}
      <section className="py-16 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-2xl font-bold mb-8">Featured <span className="text-[#C5A028]">Alumni</span></h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {featuredStories.map((story) => (
              <Link 
                key={story.id}
                to={`/stories/${story.id}`}
                data-testid={`featured-story-${story.id}`}
                className="group relative bg-white/5 border border-white/10 overflow-hidden hover:bg-white/10 transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={story.image} 
                    alt={story.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {story.videoUrl && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-[#C5A028] rounded-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-[#1A1A1A] ml-1" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-1">{story.name}</h3>
                  <p className="text-[#C5A028] text-sm font-medium mb-3">{story.program}, {story.graduationYear}</p>
                  <p className="text-white/60 text-sm mb-4">{story.currentRole}</p>
                  <Quote className="w-6 h-6 text-[#C5A028] mb-2" />
                  <p className="text-white/80 text-sm italic line-clamp-3">"{story.quote}"</p>
                  <span className="inline-flex items-center gap-2 text-[#C5A028] font-semibold text-sm mt-4 group-hover:gap-4 transition-all duration-300">
                    <ArrowRight className="w-4 h-4" />
                    READ FULL STORY
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white border-b border-[#E5E5E5] sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-[#666666] font-medium">Filter by Program:</span>
            <div className="flex flex-wrap gap-2">
              {programs.map((program) => (
                <button
                  key={program}
                  onClick={() => setSelectedProgram(program)}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                    selectedProgram === program
                      ? 'bg-[#0D4A2E] text-white'
                      : 'bg-[#F5F5F5] text-[#666666] hover:bg-[#0D4A2E] hover:text-white'
                  }`}
                >
                  {program === 'All' ? 'All Programs' : program}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* All Stories Grid */}
      <section className="py-16 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0D4A2E] mb-8">All Stories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredStories.map((story) => (
              <Link 
                key={story.id}
                to={`/stories/${story.id}`}
                data-testid={`story-card-${story.id}`}
                className="group bg-white overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex gap-4 p-6">
                  <img 
                    src={story.image} 
                    alt={story.name}
                    className="w-20 h-20 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex-grow min-w-0">
                    <h3 className="font-bold text-[#0D4A2E] group-hover:text-[#C5A028] transition-colors duration-300">
                      {story.name}
                    </h3>
                    <p className="text-[#C5A028] text-sm font-medium">{story.graduationYear}</p>
                    <p className="text-[#666666] text-sm truncate">{story.program}</p>
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <div className="flex items-center gap-2 text-sm text-[#666666] mb-3">
                    <Briefcase className="w-4 h-4" />
                    <span className="truncate">{story.currentRole}</span>
                  </div>
                  <p className="text-[#666666] text-sm italic line-clamp-2">"{story.quote}"</p>
                  <span className="inline-flex items-center gap-2 text-[#C5A028] font-semibold text-sm mt-4 group-hover:gap-4 transition-all duration-300">
                    <ArrowRight className="w-4 h-4" />
                    READ STORY
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Share Your Story CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#0D4A2E] mb-4">Are You a CIU Graduate?</h2>
          <p className="text-[#666666] mb-8">
            We'd love to hear your story! Share how CEPRES has impacted your career and inspire future students.
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200"
          >
            <ArrowRight className="w-5 h-5" />
            SHARE YOUR STORY
          </Link>
        </div>
      </section>
    </div>
  );
}

// Single Story View
function StoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const story = getStoryById(id);

  if (!story) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0D4A2E] mb-4">Story Not Found</h1>
          <Link to="/stories" className="text-[#C5A028] hover:underline">Return to Stories</Link>
        </div>
      </div>
    );
  }

  // Get other stories from same program
  const relatedStories = studentStories
    .filter(s => s.program === story.program && s.id !== story.id)
    .slice(0, 2);

  return (
    <div data-testid="story-detail-page" className="min-h-screen pt-20">
      <SEO 
        title={`${story.name} - ${story.program} Alumni Story`}
        description={`Read ${story.name}'s inspiring journey: ${story.currentRole}. Class of ${story.graduationYear}, ${story.program}. "${story.quote}"`}
        url={`/stories/${story.id}`}
        type="article"
        image={story.image}
      />
      {/* Hero */}
      <section className="relative py-16 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <button 
            onClick={() => navigate('/stories')}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-8 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Stories
          </button>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            {/* Profile Card */}
            <div className="lg:col-span-1">
              <div className="bg-white/5 border border-white/10 p-8 text-center">
                <img 
                  src={story.image} 
                  alt={story.name}
                  className="w-32 h-32 rounded-full object-cover mx-auto mb-6 border-4 border-[#C5A028]"
                />
                <h1 className="text-2xl font-bold mb-2">{story.name}</h1>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-center gap-2 text-[#C5A028]">
                    <GraduationCap className="w-4 h-4" />
                    <span>{story.program}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-white/60">
                    <span>Class of {story.graduationYear}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-white/60">
                    <Briefcase className="w-4 h-4" />
                    <span>{story.currentRole}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-white/60">
                    <MapPin className="w-4 h-4" />
                    <span>{story.location}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Quote */}
            <div className="lg:col-span-2">
              <Quote className="w-12 h-12 text-[#C5A028] mb-4" />
              <blockquote className="text-2xl lg:text-3xl font-light text-white leading-relaxed italic">
                "{story.quote}"
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Story Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0D4A2E] mb-8">
            {story.name}'s Journey
          </h2>
          <div className="prose prose-lg max-w-none">
            {story.story.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-[#1A1A1A] leading-relaxed mb-6">
                {paragraph.trim()}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Related Stories */}
      {relatedStories.length > 0 && (
        <section className="py-16 bg-[#F5F5F5]">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <h2 className="text-2xl font-bold text-[#0D4A2E] mb-8">
              More from {story.program}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {relatedStories.map((relatedStory) => (
                <Link 
                  key={relatedStory.id}
                  to={`/stories/${relatedStory.id}`}
                  className="group flex gap-6 bg-white p-6 hover:shadow-lg transition-shadow duration-300"
                >
                  <img 
                    src={relatedStory.image} 
                    alt={relatedStory.name}
                    className="w-20 h-20 rounded-full object-cover flex-shrink-0"
                  />
                  <div>
                    <h3 className="font-bold text-[#0D4A2E] group-hover:text-[#C5A028] transition-colors duration-300">
                      {relatedStory.name}
                    </h3>
                    <p className="text-[#666666] text-sm">{relatedStory.currentRole}</p>
                    <p className="text-[#666666] text-sm italic mt-2 line-clamp-2">"{relatedStory.quote}"</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Start Your Own Journey</h2>
          <p className="text-white/70 mb-8">
            Join the CEPRES community and create your own success story
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/programs"
              className="inline-flex items-center gap-2 bg-white text-[#0D4A2E] px-8 py-4 font-bold hover:bg-[#F5F5F5] transition-colors duration-200"
            >
              <ArrowRight className="w-5 h-5" />
              VIEW PROGRAMS
            </Link>
            <a 
              href="https://cnte.ciu.edu.lr/apply"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200"
            >
              APPLY NOW
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

// Main Export
export default function Stories() {
  const { id } = useParams();
  return id ? <StoryDetail /> : <StoriesList />;
}

export { StoriesList, StoryDetail };
